// Everything this household has put on the list before, for quick add
// (components/list/QuickAdd.tsx). Like Running low (lib/restock.ts), the
// history is the list itself: Finish trip and delete soft-delete rows, so the
// soft-deleted rows are every name ever added — no history table. Names are
// compared with itemKey (lib/normalize.ts), so "Ägg" last month and "eggs"
// today are one entry, and so are "potatis fast" and "fast potatis".

import { itemKey } from './normalize'
import { tripCount } from './restock'
import { indexItems, isSureMatch, rankedMatches, type Indexed } from './search'
import { userLocale } from './locale'
import type { Item } from './supabase'

type Named = { name: string }

export type HistoryRow = Pick<Item, 'name' | 'category_id' | 'quantity' | 'updated_at'> & Partial<Pick<Item, 'done' | 'done_at'>>

export interface CatalogEntry {
  key: string
  /** The latest spelling. */
  name: string
  /** The latest category it was filed under. */
  categoryId: string | null
  /** The latest amount — shown as a hint, never reused. */
  quantity: string | null
}

/**
 * One entry per name, leaving out what's on the list now: what was bought on
 * the most trips first (Running low's count, lib/restock.ts `tripCount`), then
 * the most recently used. Quick add keeps that order within a match's rank.
 */
export function buildCatalog(past: HistoryRow[], live: Pick<Item, 'name'>[]): CatalogEntry[] {
  const onList = new Set(live.map(i => itemKey(i.name)))
  const newestFirst = [...past].sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''))
  const byKey = new Map<string, CatalogEntry>()
  const bought = new Map<string, Date[]>()
  for (const row of newestFirst) {
    const key = itemKey(row.name)
    if (!key || onList.has(key)) continue
    if (row.done && row.done_at) bought.set(key, [...(bought.get(key) ?? []), new Date(row.done_at)])
    const entry = byKey.get(key)
    if (!entry) byKey.set(key, { key, name: row.name, categoryId: row.category_id, quantity: row.quantity })
    else if (!entry.categoryId && row.category_id) entry.categoryId = row.category_id
  }
  const trips = new Map([...bought].map(([key, times]) => [key, tripCount(times)]))
  // Stable: equal counts stay newest first.
  return [...byKey.values()].sort((a, b) => (trips.get(b.key) ?? 0) - (trips.get(a.key) ?? 0))
}

/**
 * itemKey → the household's latest spelling of it, from past and live rows
 * alike. What a recipe's ingredient is called when it goes on the list, so
 * the household's history keeps one name per thing (lib/recipeBook.ts
 * `combineNeeds`).
 */
export function buildSpellings(rows: Pick<Item, 'name' | 'updated_at'>[]): Map<string, string> {
  const latest = new Map<string, { name: string; at: string }>()
  for (const row of rows) {
    const key = itemKey(row.name)
    if (!key) continue
    const at = row.updated_at ?? ''
    const prev = latest.get(key)
    if (!prev || at >= prev.at) latest.set(key, { name: row.name.trim(), at })
  }
  return new Map([...latest].map(([key, v]) => [key, v.name]))
}

/**
 * The name the household already uses for `name`, matched the way quick add
 * matches (lib/search.ts) but only when it is sure: the same name, EN↔SV and
 * word order aside, or one slip away. A name that merely starts with or
 * contains it is not taken ("pasta" stays, though "Pastasås" is known) — it is
 * one of `nameSuggestions` instead. Null when nothing is that close.
 */
export function knownName(name: string, known: Indexed<Named>[], locale = userLocale()): string | null {
  return rankedMatches(known, name, locale, known.length).find(isSureMatch)?.item.name ?? null
}

/** Names close to `name` that aren't sure matches — quick add's suggestions, to tap. */
export function nameSuggestions(name: string, known: Indexed<Named>[], limit = 2, locale = userLocale()): string[] {
  const typed = name.trim().toLocaleLowerCase()
  return rankedMatches(known, name, locale, known.length)
    .filter(m => !isSureMatch(m) && m.item.name.toLocaleLowerCase() !== typed)
    .slice(0, limit)
    .map(m => m.item.name)
}

/** The household's spelling of `name`, or `name` itself when it is new. */
export function spellingFor(name: string, known: Indexed<Named>[]): string {
  return knownName(name, known) ?? name
}

/** The household's names (buildSpellings), indexed once for knownName and nameSuggestions. */
export const indexNames = (names: Iterable<string>): Indexed<Named>[] => indexItems([...names].map(name => ({ name })))

export interface HistoryGroup {
  key: string
  /** The latest spelling, shown. */
  name: string
  /** Every spelling of it in the history — what forget_items deletes by. */
  names: string[]
  /** Past rows of it. */
  count: number
}

/**
 * The history as Settings › Item history lists it: one row per name (itemKey,
 * as the catalog), alphabetical. Unlike `buildCatalog` it keeps names that are
 * on the list now — their past rows are history too.
 */
export function historyGroups(past: Pick<Item, 'name' | 'updated_at'>[], locale?: string): HistoryGroup[] {
  const newestFirst = [...past].sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''))
  const byKey = new Map<string, HistoryGroup>()
  for (const row of newestFirst) {
    const key = itemKey(row.name)
    if (!key) continue
    const group = byKey.get(key)
    if (!group) byKey.set(key, { key, name: row.name, names: [row.name], count: 1 })
    else {
      group.count++
      if (!group.names.includes(row.name)) group.names.push(row.name)
    }
  }
  return [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name, locale, { sensitivity: 'base' }))
}

/** A name the household uses, for the name fields' suggestions (components/choices/NameField). */
export interface KnownName {
  name: string
  /** Where it comes from: "On your list" or "From before". */
  where: string
}

/** The names a row can be pointed at: the list first, then names from before, one per thing. */
export function knownNames(items: { name: string; deleted_at?: string | null }[], before: { name: string }[]): KnownName[] {
  const seen = new Set<string>()
  const out: KnownName[] = []
  const take = (name: string, where: string) => {
    const key = itemKey(name)
    if (!key || seen.has(key)) return
    seen.add(key)
    out.push({ name, where })
  }
  items.forEach(i => { if (!i.deleted_at) take(i.name, 'On your list') })
  before.forEach(b => take(b.name, 'From before'))
  return out
}
